/**
 * Game Loop
 * 
 * Real-time update loop that drives the game.
 * Updates resources, construction, and other time-based systems.
 */

export class GameLoop {
    constructor(gameState) {
        this.gameState = gameState;
        this.lastUpdate = Date.now();
        this.running = false;
        this.animationFrameId = null;
        
        // Update intervals (in milliseconds)
        this.resourceUpdateInterval = 1000; // Update resources every second
        this.constructionUpdateInterval = 1000; // Check construction every second
        this.lastResourceUpdate = 0;
        this.lastConstructionUpdate = 0;
    }
    
    /**
     * Start the game loop
     */
    start() {
        if (this.running) {
            return;
        }
        
        this.running = true;
        this.lastUpdate = Date.now();
        this.lastResourceUpdate = this.lastUpdate;
        this.lastConstructionUpdate = this.lastUpdate;
        this.loop();
    }
    
    /**
     * Stop the game loop
     */
    stop() {
        this.running = false;
        if (this.animationFrameId !== null) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
        }
    }
    
    /**
     * Main game loop
     */
    loop() {
        if (!this.running) {
            return;
        }
        
        const now = Date.now();
        const deltaTime = now - this.lastUpdate;
        this.lastUpdate = now;
        
        // Update resources at specified interval
        if (now - this.lastResourceUpdate >= this.resourceUpdateInterval) {
            const resourceDelta = now - this.lastResourceUpdate;
            this.gameState.updateResources(resourceDelta);
            this.lastResourceUpdate = now;
        }
        
        // Update construction queue at specified interval
        if (now - this.lastConstructionUpdate >= this.constructionUpdateInterval) {
            const constructionDelta = now - this.lastConstructionUpdate;
            this.gameState.update(constructionDelta);
            this.lastConstructionUpdate = now;
        }
        
        // Always update timers (they handle their own timing)
        this.gameState.timerManager.update(deltaTime);
        
        // Continue loop
        this.animationFrameId = requestAnimationFrame(() => this.loop());
    }
    
    /**
     * Set resource update interval
     * @param {number} intervalMs - Interval in milliseconds
     */
    setResourceUpdateInterval(intervalMs) {
        this.resourceUpdateInterval = Math.max(100, intervalMs); // Minimum 100ms
    }
    
    /**
     * Set construction update interval
     * @param {number} intervalMs - Interval in milliseconds
     */
    setConstructionUpdateInterval(intervalMs) {
        this.constructionUpdateInterval = Math.max(100, intervalMs); // Minimum 100ms
    }
}

